import isEqual from 'lodash/isEqual';
import intersection from 'lodash/intersection';
import {
  isDark as isDarkColor,
  getBlackwhiteConstrastingColor,
  getClosestCandidateColorByRGB,
  getBrownForAnchorColor,
  isValidColor,
  findClosestColorConsideringBrightness
} from '~/utils/colors';
import {
  mapToColorRecord,
  sortColors,
  getViewImage,
  getAlternateVariant
} from '~/utils/catalog';
import {
  getProductIdViewIdKey,
  getValidProductColors,
  getStyleIdProductColorIdKey
} from '~/utils/product';
import {MAXIMUM_PRODUCT_COUNT as FUNDRAISING_MAXIMUM_PRODUCT_COUNT} from '~/constants/FundraisingConstants';
import {
  DEFAULT_MAXIMUM_PRODUCT_COUNT,
  DEFAULT_VIEW_IDS,
  DEFAULT_VIEWS,
  ID_VIEW_MAP,
  SLEEVE_COLOR_LIMIT,
  SLEEVE_VIEW_IDS,
  VIEW_ID_MAP,
  MASKS_CATEGORY_ID,
  MULTI_ITEM_STYLE_OVERRIDES
} from '~/constants/ProductConstants';
import RemoteServices from '~/constants/RemoteServices';
import Color from '~/data/records/Color';
import {DEFAULT_QUOTE_QUANTITY, WCIBMOS} from '~/constants/QuoteConstants';
import {createSelector} from 'reselect';
import {
  decorationOverridesEnabled,
  isSleeveDecoEligible
} from '~/selectors/ConfigSelectors';
import {
  hasSleeveDesign,
  isBlank,
  selectedViewId,
  viewColors,
  viewIcons
} from '~/selectors/CanvasSelectors';
import {
  areComplementProducts,
  areCompatibleProducts,
  getAlternatives,
  getColor,
  getCompatibleStyles,
  getComplements,
  getStyle,
  getStyles,
  getErrorFreeStyles
} from '~/selectors/CatalogSelectors';
import {isFundraisingMode} from '~/selectors/LabModeSelectors';
import {
  getDroppedView,
  getItemizedPrices,
  getItemizedPricesWithoutDroppedView,
  getQuantities,
  getQuantitiesJs,
  getQuoteItems,
  getRealTimeItemizedPrices,
  getRealTimeTotalPrice,
  getSleeveViewLessPrice,
  getSleeveViewLessSaveAmount,
  getTotalPrice,
  getTotalQuantity,
  getHasDroppedView,
  hasTouchedQuantities
} from '~/selectors/QuoteSelectors';
import {getNamesAndNumbersCount} from '~/selectors/NamesAndNumbersSelectors';
import {isFullColorUpload} from '~/data/accessors/icon';
import {recordFromMmsColor} from '~/data/records/Color';
import {INK_COLORS, ORDERED_INK_COLORS} from '~/constants/InkConstants';
import {Color as MMSColor} from 'mms_client_js';
import {matchesDecorationOverride} from '../utils/decorationOverrides';
const ELEVATED_COMPLEMENT_TYPES = ["Women's", 'Unisex', 'Tall'];

export const getProductState = state => {
  if (!state.product) {
    return state;
  }

  return state.product.present || state.product;
};

export const getAnchorStyleId = state =>
  getProductState(state).get('anchorStyleId');
export const getCategoryPaths = state =>
  getProductState(state).get('categoryPaths');
export const getCurrentColorId = state =>
  getProductState(state).get('currentColorId');
export const getHasInvalidProductImage = state =>
  getProductState(state).get('hasInvalidProductImage');
export const getSelections = state => getProductState(state).get('selections');
export const getSelectionsToArray = state => getSelections(state).toArray();

const getLastSavedSelections = state =>
  getProductState(state).get('lastSavedSelections');

export const getStyleIdFromSelection = createSelector(
  [getCurrentColorId, getSelections],
  (colorId, selections) => {
    const selection = selections.find(sel => sel.get('colorId') === colorId);
    return selection ? selection.get('styleId') : undefined;
  }
);

export const getColorSelections = createSelector([getSelections], selections =>
  selections.map(s => s.get('colorId')).toArray()
);

export const getStyleSelections = createSelector([getSelections], selections =>
  selections
    .map(s => s.get('styleId'))
    .toSet()
    .toArray()
);

export const getProductFromSelection = (state, {colorId}) => {
  const selections = getSelectionsToArray(state);
  return selections?.find(sel => sel?.get('colorId') === colorId);
};

export const getViewsForStyle = (state, {styleId}) => {
  if (!styleId) return [];
  const selectedStyleIds = getStyleSelections(state);
  const selectedStyles = selectedStyleIds.map(id => {
    return getStyle(state, {styleId: id});
  });
  const selectedStylesViews = selectedStyles.map(s => s.views);
  const eligibleViewIds = selectedStylesViews.reduce(
    (viewIds, selectedStyleViews) => {
      if (viewIds.length === 0) {
        viewIds = selectedStyleViews.map(view => view.id);
      } else {
        viewIds = intersection(
          viewIds,
          selectedStyleViews.map(view => view.id)
        );
      }
      return viewIds;
    },
    []
  );
  return getStyle(state, {styleId}).views.filter(view =>
    eligibleViewIds.includes(view.id)
  );
};

export const isSingleStyle = createSelector(
  [getStyleSelections, getColorSelections],
  (selections, colors) => selections.length === 1 && colors.length === 1
);

export const getColorSelectionsForStyle = (state, styleId) => {
  const colorSelectionsForStyle = getSelections(state).filter(
    selection => selection.get('styleId') === styleId
  );
  return colorSelectionsForStyle.map(s => s.get('colorId')).toArray();
};

export const getCurrentSelection = createSelector(
  [getCurrentColorId, getSelections],
  (currentColorId, selections) => {
    return selections.find(
      selection => currentColorId === selection.get('colorId')
    );
  }
);

/**
 * i.e. {12345: "SM"}
 */
export const getFirstSizePerSelection = state => {
  const selections = getSelectionsToArray(state);

  return selections.reduce((acc, selection) => {
    const firstSize = getColor(state, selection)?.sizes[0];
    acc[selection.colorId] = firstSize;
    return acc;
  }, {});
};

export const getCategoryPathForSelection = createSelector(
  [getCurrentSelection, getCategoryPaths],
  (selection, paths) => paths.get(selection.styleId)
);

export const getStyleCategoryPath = (state, styleId) => {
  const paths = getCategoryPaths(state);
  return paths.get(styleId);
};

export const getCurrentSelectionIndex = createSelector(
  [getCurrentColorId, getSelections],
  (colorId, selections) =>
    selections.findIndex(selection => colorId === selection.get('colorId'))
);

export const getSelectedStyleColors = createSelector(
  [getErrorFreeStyles, getCurrentSelection],
  (styles, selection) => {
    const selectedStyle = styles.get(selection.get('styleId'));
    return selectedStyle
      ? selectedStyle.colors.filter(color => color.status === 'active')
      : [];
  }
);

export const isAnchorSingleSize = state => {
  const style = getAnchorStyle(state);
  return style?.sizes?.length === 1;
};

//
// Join Selectors.
// These join data from the catalog store with product store
//

/**
 * i.e. a single-color glass cup with only one available size
 */
export const isSingleSizeAndStyle = createSelector(
  [isAnchorSingleSize, isSingleStyle],
  (singleSize, singleStyle) => singleSize && singleStyle
);

export function getProductColor(product, colorId) {
  if (product && !product.isFetching) {
    const rawColor = product.colors.find(color => color.id === colorId);
    return rawColor ? recordFromMmsColor(rawColor) : null;
  } else {
    return undefined;
  }
}

export const getAnchorStyle = createSelector(
  [getAnchorStyleId, getErrorFreeStyles],
  (anchorStyleId, styles) => styles.get(anchorStyleId)
);

export const getAnchorColor = createSelector(
  [getAnchorStyle, getSelections],
  (anchorStyle, selections) => {
    const firstAnchorSelection =
      anchorStyle &&
      selections.find(selection => selection.styleId === anchorStyle.id);
    return (
      firstAnchorSelection &&
      anchorStyle.colors.find(
        color => color.id === firstAnchorSelection.colorId
      )
    );
  }
);

export const getAnchorStyleType = createSelector(
  [getAnchorStyle],
  anchorStyle => anchorStyle?.style_type
);

export const getAnchorDecorationMethod = createSelector(
  [getAnchorStyle],
  anchorStyle => anchorStyle?.decoration_method
);

export const getAnchorMaterial = createSelector(
  [getAnchorStyle],
  anchorStyle => anchorStyle?.material
);

export const isAnchorColorDark = createSelector(
  [getAnchorColor],
  anchorColor => {
    const productColor = new Color(anchorColor?.rgbs[0]);
    return isDarkColor(productColor);
  }
);

export const anchorHasWoodenMaterial = createSelector(
  [getAnchorStyle],
  anchorStyle => {
    return (
      anchorStyle?.material?.toLowerCase()?.includes('wood') ||
      anchorStyle?.material?.toLowerCase()?.includes('bamboo')
    );
  }
);

export const anchorIsLeatherPatch = createSelector(
  [getAnchorStyle],
  anchorStyle => /leather.*patch/.test(anchorStyle?.name?.toLowerCase())
);

export const isAnchorDebossed = createSelector(
  [getAnchorDecorationMethod],
  anchorDecorationMethod => anchorDecorationMethod === 'debossed'
);

export const isAnchorEmbossed = createSelector(
  [getAnchorDecorationMethod],
  anchorDecorationMethod => anchorDecorationMethod === 'embossed'
);

export const isAnchorEmbroidered = createSelector(
  [getAnchorDecorationMethod],
  anchorDecorationMethod => anchorDecorationMethod === 'embroidery'
);

export const isAnchorScreenPrinted = createSelector(
  [getAnchorDecorationMethod],
  anchorDecorationMethod => anchorDecorationMethod === 'screen'
);

export const isSleeveDecoEligibleStyle = state => styleId => {
  const style = getStyleDataForStyleId(state)(styleId);

  if (!style) return false;

  const hasSleevePrintArea = style.additional_print_areas === 'sleeve';
  const productColor = style.colors?.find(
    color => color?.id === getCurrentColorId(state)
  );
  const hasSleeveView = !!style.views?.find(
    view => view?.name?.toLowerCase().indexOf('sleeve') > 0
  );
  const colorHasAllProductViewImages = styleColorHasAllProductViewImages({
    style,
    styleColor: productColor
  });

  return (
    hasSleevePrintArea &&
    hasSleeveView &&
    (colorHasAllProductViewImages || canBorrowProductViewImages(state)())
  );
};

export const canBorrowProductViewImages = state => () => {
  return !!getSimilarProductWithImages(state);
};

// use getStyles, this selector needs access to errors
export const getSelectedProduct = createSelector(
  [getStyles, getCurrentSelection],
  (styles, selection) => selection && styles.get(selection.styleId)
);

export const getSelectedProducts = createSelector(
  [getErrorFreeStyles, getSelections],
  (styles, selections) => selections.map(s => styles.get(s.styleId))
);

export const getSelectedProductsJs = createSelector(
  [getSelectedProducts],
  styles => styles.toJS()
);

export const getProductByStyleId = state => styleId => {
  const products = getSelectedProducts(state);
  return products?.find(product => product.id === styleId);
};

export const getVisibleSelectionsToArray = createSelector(
  [getSelections],
  selections => selections.filter(selection => selection.visible).toArray()
);

export const getVisibleSelectedProducts = createSelector(
  [getErrorFreeStyles, getVisibleSelectionsToArray],
  (styles, selections) => selections.map(s => styles.get(s.styleId))
);

export const getAddCartItemsToSelectionEligible = state => cartItemColorIds => {
  const selections = getSelectionsToArray(state);
  const cartItemsInSelection = cartItemColorIds.filter(cartItemColorId =>
    selections.find(
      selection => String(selection.get('colorId')) === String(cartItemColorId)
    )
  );
  return cartItemsInSelection.length < 1; // return true if empty, false if 1 or greater
};

export const getSelectedProductsArray = createSelector(
  [getErrorFreeStyles, getSelections],
  (styles, selections) =>
    selections
      .map(s => styles.get(s.styleId))
      .filter(s => !!s)
      .toArray()
);

export const getProductsFromCurrentSelection = createSelector(
  [getSelections, getSelectedProductsArray],
  (currentSelections, selectedStyles) => {
    return currentSelections.reduce((acc, selection) => {
      const style = selectedStyles.find(
        style => style.id === selection.styleId
      );
      const color = style.colors.find(color => color.id === selection.colorId);
      if (style && color) {
        acc.push({style, color});
      }
      return acc;
    }, []);
  }
);

export const getSelectedProductsIds = createSelector(
  [getSelectedProducts],
  products => products?.map(product => product?.id).toArray() || []
);

export const didSelectedStyleLoadError = createSelector(
  [getSelectedProduct],
  product => {
    return !product || !!product.errors;
  }
);

export const isSelectedStyleLoaded = createSelector(
  [getSelectedProduct],
  product => !!(product && !product.errors)
);

export const getErrorFreeSelectedProduct = createSelector(
  [getSelectedProduct, didSelectedStyleLoadError],
  (selectedProduct, loadError) => (loadError ? null : selectedProduct)
);

export const getSelectedProductCurrentColor = createSelector(
  [getErrorFreeSelectedProduct, getCurrentColorId],
  (style, colorId) => {
    return style?.colors.find(color => color.id === colorId);
  }
);

export const getSelectedProductCategoryName = createSelector(
  [getSelectedProduct],
  style => style?.primary_category_name
);

export const getSelectedProductCategoryId = createSelector(
  [getSelectedProduct],
  style => style?.primary_category_id
);

export const getSelectedProductSizes = createSelector(
  [getErrorFreeSelectedProduct],
  product => product?.sizes
);

export const getSelectedProductColor = createSelector(
  [getSelectedProduct, getCurrentColorId],
  (product, productKey) => {
    return getProductColor(product, productKey);
  }
);

export const getSelectedProductColorSizes = createSelector(
  [getErrorFreeSelectedProduct, getSelectedProductColor],
  (product, productColor) =>
    product?.colors.find(c => c.id === productColor.id).sizes || []
);

export const getSelectedProductColorSizesWithStatus = createSelector(
  [getSelectedProduct, getSelectedProductColor],
  (product, productColor) =>
    product?.colors?.find(c => c.id === productColor.id).sizesWithStatus || []
);

export const getSelectedProductColorMinimum = createSelector(
  [getSelectedProductCurrentColor],
  color => color?.minimum_quantity
);

export const getSelectedProductMinimum = createSelector(
  [getErrorFreeSelectedProduct],
  product => product?.minimum_quantity
);

export const getSelectedProductType = createSelector(
  [getSelectedProduct],
  product => product?.product_type
);

export const getSelectedProductStyleType = createSelector(
  [getSelectedProduct],
  product => product?.style_type
);

export const getSelectedProductSupportingImages = createSelector(
  [getSelectedProduct],
  product => product?.supporting_images
);

export const getSelectedProductCatalogImages = createSelector(
  [getSelectedProduct],
  product => product?.catalog_detail_image_md5
);

export const getSelectedColors = createSelector(
  [getSelectedProducts, getSelections],
  (products, selections) =>
    selections
      .map((s, i) => getProductColor(products.get(i), s.colorId))
      .toArray()
);

export const _modifyWatchCategoryID = 620;
export const hasOverlay = createSelector(
  [getErrorFreeSelectedProduct],
  product => product?.category_ids?.includes(_modifyWatchCategoryID)
);

export const allProductsHaveSameViews = createSelector(
  [getSelectedProducts],
  selectedProducts => {
    let matching = true;
    const products = selectedProducts?.toArray() || [];
    if (products.length === 0 || !products[0]) {
      return matching;
    }
    const anchorViews = products[0].views.map(view => view.name);
    products.slice(1).some(product => {
      const views = product?.views?.map(view => view.name);
      if (!isEqual(views, anchorViews)) {
        matching = false;
      }
      // Break the loop
      if (!matching) return true;
    });
    return matching;
  }
);

// Joins selections, product data, and color data into one structure.
export const getAllProductsData = createSelector(
  [getSelections, getSelectedProducts, getSelectedColors],
  (selections, selectedStyles, colors) => {
    return selections.toArray().map((s, i) => {
      return {
        selection: s,
        style: selectedStyles.get(i),
        color: colors[i]
      };
    });
  }
);

// Joins selections, product data, and color data into one structure.
export const getAllProductsDataForQuote = createSelector(
  [getSelectionsToArray, getSelectedProducts, getSelectedColors],
  (selections, selectedStyles, colors) => {
    return selections
      .map((s, i) => {
        return {
          selection: s,
          style: selectedStyles.get(i),
          color: colors[i],
          visible: s.visible
        };
      })
      .filter(selection => selection.style && selection.color);
  }
);

export const getProductsData = createSelector(
  [getAllProductsData],
  productsData => productsData?.filter(data => data.selection.visible) || []
);

export const getProductsDataForQuote = createSelector(
  [getAllProductsDataForQuote],
  dataForQuote => dataForQuote?.filter(data => data.selection.visible) || []
);

// Is this a multi-item quote?
export const isMultiItem = createSelector(
  [getQuoteItems],
  quoteItems => quoteItems.size > 1
);

export const allowsNamesAndNumbers = createSelector(
  [getSelectedProduct],
  product => !!product?.sponsorship
);

export const allowsPriceBlank = createSelector(
  [getSelectedProduct],
  product => !!product?.allow_blank
);

// Use getStyles because this selector needs access to errors
export const isAnyStyleLoaded = createSelector(
  [getSelections, getStyles],
  (selections, styles) =>
    selections.some(selection => {
      return styles.find((style, styleId) => {
        return styleId === selection.get('styleId') && !style.errors;
      });
    })
);

export const isSelectedProductMask = createSelector(
  [getErrorFreeSelectedProduct],
  product =>
    product &&
    Object.keys(product).length > 0 &&
    product.category_ids.some(id => id === MASKS_CATEGORY_ID) &&
    product.colors.length > 1
);

export const isSelectedMultiItemEnabled = createSelector(
  [getErrorFreeSelectedProduct, isSelectedStyleLoaded],
  (product, styleLoaded) =>
    styleLoaded &&
    product.colors.length > 1 &&
    (!product.specialty || MULTI_ITEM_STYLE_OVERRIDES.includes(product.id))
);

export const isSelectedProductEmbroidered = createSelector(
  [getSelectedProduct],
  product => product?.decoration_method === 'embroidery'
);

export const getSelectedProductName = createSelector(
  [getSelectedProduct],
  product => product?.name
);

export const getSelectedProductId = createSelector(
  [getSelectedProduct],
  product => product?.id
);

export const getSelectedProductRatingScore = createSelector(
  [getSelectedProduct],
  product => product && Number(product.rating_score)
);

export const getSelectedProductRatingCount = createSelector(
  [getSelectedProduct],
  product => product && Number(product.rating_count)
);

export const getDefaultIconColor = createSelector(
  [getSelectedProductColor, getAnchorMaterial],
  getBlackwhiteConstrastingColor
);

export const getSortedProductColors = createSelector(
  [getErrorFreeSelectedProduct],
  sortColors
);

export const getProductColors = createSelector(
  [getSortedProductColors],
  mapToColorRecord
);

export const getActiveProductColors = createSelector(
  [getSortedProductColors],
  sortedColors =>
    mapToColorRecord(sortedColors.filter(color => color.status === 'active'))
);

export const getActiveSinglesProductColors = createSelector(
  [getSortedProductColors],
  sortedColors =>
    mapToColorRecord(
      sortedColors.filter(
        color => color.status === 'active' && color.singles_enabled
      )
    )
);

export const getActiveBulkProductColors = createSelector(
  [getSortedProductColors],
  sortedColors =>
    mapToColorRecord(
      sortedColors.filter(
        color => color.status === 'active' && !color.singles_enabled
      )
    )
);

export const getActiveProductColorsCount = createSelector(
  [getActiveProductColors],
  colors => colors.length
);

export const getSelectedProductQuantity = createSelector(
  [getCurrentColorId, getQuantities],
  (colorId, quantities) => {
    const sizes = quantities.find((value, key) => key === colorId);
    return sizes?.reduce((acc, count) => acc + count, 0);
  }
);

export const getRealTimePriceQuantity = createSelector(
  [hasTouchedQuantities, getSelectedProductQuantity],
  (touched, quantity) => quantity || (touched ? 0 : DEFAULT_QUOTE_QUANTITY)
);

export const getSelectedProductItemPrice = createSelector(
  [getCurrentColorId, getTotalPrice, getItemizedPrices],
  (colorId, totalPrice, itemizedPrices) => {
    const itemizedPrice = itemizedPrices && itemizedPrices.get(colorId);
    // Total price will be null for any error response from the quote service
    return totalPrice && itemizedPrice?.get('itemPrice');
  }
);

export const getSelectedProductRealTimeItemPrice = createSelector(
  [getCurrentColorId, getRealTimeTotalPrice, getRealTimeItemizedPrices],
  (colorId, totalPrice, itemizedPrices) => {
    const itemizedPrice = itemizedPrices && itemizedPrices.get(colorId);
    // Total price will be null for any error response from the quote service
    return totalPrice && itemizedPrice?.get('itemPrice');
  }
);

export const getProductViews = state => product => {
  const sleevePrintEligible = isSleeveDecoEligible(state);
  const allProductsSameViews = allProductsHaveSameViews(state);

  const defaultViews =
    product?.views.filter(view => DEFAULT_VIEWS.includes(view.name)) || [];
  const defaultViewsWithSleeves =
    product?.views.filter(view =>
      Object.keys(VIEW_ID_MAP).includes(view.name)
    ) || [];

  if (allProductsSameViews && sleevePrintEligible) {
    return (
      product &&
      Object.values(VIEW_ID_MAP)
        .map(viewId => product.views.find(view => view.id === viewId))
        .filter(v => v !== undefined)
    );
  } else if (!allProductsSameViews) {
    const intersectingViewsNames = getIntersectingProductViewNames(state);
    return intersectingViewsNames && sleevePrintEligible
      ? defaultViewsWithSleeves.filter(view =>
          intersectingViewsNames.includes(view.name)
        )
      : defaultViews.filter(view => intersectingViewsNames.includes(view.name));
  } else {
    return defaultViews;
  }
};

export const getSelectedProductsSlides = createSelector(
  [getProductsFromCurrentSelection, getProductViews, isSleeveDecoEligibleStyle],
  (products, getViews, getSleeveDecoEligible) => {
    return [
      ...products.map(product => ({
        product: product.style,
        color: product.color,
        views: getViews(product.style),
        isSleeveDecoEligible: getSleeveDecoEligible(product.style.id)
      }))
    ];
  }
);

export const getAnchorViews = state => {
  const anchorProduct = getAnchorStyle(state);
  return getProductViews(state)(anchorProduct);
};

export const getIntersectingProductViewNames = createSelector(
  [getSelectedProducts],
  selectedProducts => {
    const products = selectedProducts?.toArray();

    if (!products || (products && products.length === 0)) {
      return [];
    }

    if (products.length === 1) {
      return products[0].views;
    }

    const viewsCounterMap = {};

    products.forEach(product => {
      if (product?.views) {
        product.views.forEach(view => {
          if (!viewsCounterMap[view.name]) {
            viewsCounterMap[view.name] = 0;
          }
          viewsCounterMap[view.name] += 1;
        });
      }
    });
    const intersectingViewsNames = Object.keys(viewsCounterMap).filter(
      viewName => viewsCounterMap[viewName] === products.length
    );

    return intersectingViewsNames;
  }
);

// always get anchor product's views as any complementary style has been vetted by merch.
export const getViewsList = createSelector(
  [getAnchorStyle, isSleeveDecoEligible],
  (style, sleevePrintEligible) => {
    if (sleevePrintEligible) {
      return (
        style?.views &&
        Object.values(VIEW_ID_MAP)
          .map(viewId => style.views.find(view => view.id === viewId))
          .filter(v => v !== undefined)
      );
    } else {
      return style?.views?.filter(view => DEFAULT_VIEWS.includes(view.name));
    }
  }
);

export const getView = (state, {viewIdentifier}) =>
  getViewsList(state)?.find(
    view => view.id === viewIdentifier || view.name === viewIdentifier
  );

export const getViewsOptions = createSelector([getViewsList], views =>
  views.map(view => ({name: view.name, value: view.id}))
);

export const anchorStyleHasSleeveView = createSelector(
  [getViewsList],
  viewsList => !!viewsList?.find(v => v?.name?.includes('Sleeve'))
);

export const getSelectedView = createSelector(
  [getErrorFreeSelectedProduct, getViewsList, selectedViewId],
  (selectedProduct, views = [], viewId) => {
    if (DEFAULT_VIEW_IDS.includes(viewId)) {
      return views.find(view => view.id === viewId);
    }
    return selectedProduct.views.find(view => view.id === viewId) || views[0];
  }
);

const getMinBoundingBox = state => viewData => {
  const styles = getSelectedProducts(state);
  const constraints = styles.reduce((constraints, style) => {
    if (style && style.views) {
      const view = style.views.find(view => view.name === viewData.name);
      if (view && view.constraint) {
        constraints.push(view.constraint);
      }
    }
    return constraints;
  }, []);
  if (constraints.length === 0) {
    return;
  }
  const minHeight = constraints.reduce(
    (min, c) => (c.height < min ? c.height : min),
    constraints[0].height
  );
  const minWidth = constraints.reduce(
    (min, c) => (c.width < min ? c.width : min),
    constraints[0].width
  );
  return {height: minHeight, width: minWidth};
};

export const getViewConstraint = state => selectedView => {
  if (selectedView === undefined) {
    return;
  }
  const selectedProduct = getErrorFreeSelectedProduct(state);
  const minBoundingBox = getMinBoundingBox(state)(selectedView);

  if (DEFAULT_VIEW_IDS.includes(selectedView.id)) {
    const anchorStyle = getAnchorStyle(state);
    const anchorStyleViews = (anchorStyle && anchorStyle.views) || [];
    const anchorStyleView = anchorStyleViews.find(
      view => view.id === selectedView.id
    );

    if (anchorStyleView?.constraint) {
      return anchorStyleView.constraint;
    }
  }

  const selectedViewConstraint = selectedProduct.views.find(
    v => v.name === selectedView.name
  ).constraint;

  const left =
    selectedViewConstraint.left +
    ((selectedViewConstraint.width - minBoundingBox.width) *
      selectedView.scale) /
      2;
  const right =
    selectedViewConstraint.right +
    ((selectedViewConstraint.width - minBoundingBox.width) *
      selectedView.scale) /
      2;
  const bottom =
    selectedViewConstraint.bottom +
    ((selectedViewConstraint.height - minBoundingBox.height) *
      selectedView.scale) /
      2;
  return {
    height: minBoundingBox.height,
    width: minBoundingBox.width,
    left: left,
    right: right,
    top: selectedViewConstraint.top,
    bottom: bottom
  };
};

export const getSelectedViewGuidelines = createSelector(
  [getSelectedView, getErrorFreeSelectedProduct],
  (selectedView, style) => {
    if (!selectedView || !style || !style.views) {
      return;
    }
    if (DEFAULT_VIEW_IDS.includes(selectedView.id)) {
      return selectedView.guidelines;
    }
    const view = style.views.find(view => view.name === selectedView.name);
    return view.guidelines;
  }
);

export const isDirty = createSelector(
  [getSelections, getLastSavedSelections],
  (selections, lastSavedSelections) => {
    return selections.hashCode() !== lastSavedSelections;
  }
);

export const hasMultiItem = createSelector(
  [getSelections],
  selections => selections.size > 1
);

export const getAnchorComplements = createSelector(
  [getAnchorStyleId, getComplements],
  (anchorProduct, complements) => complements.get(anchorProduct)
);

export const getAnchorAlternatives = createSelector(
  [getAnchorStyleId, getAlternatives],
  (anchorProduct, alternatives) => alternatives.get(anchorProduct)
);

export const getStyleAlternatives = state => styleId => {
  const allAlternatives = state.catalog.get('alternatives').toJS();
  return allAlternatives[styleId];
};

export const getAlternativeStylesForStyleId = state => styleId => {
  // this is to select all style objects that are styleId's alternatives
  const alternativeIdsForStyle = state.catalog.get('alternatives').toJS()[
    styleId
  ];
  const styles = getErrorFreeStyles(state);

  return (
    alternativeIdsForStyle?.map(alternativeId => ({
      alternativeId,
      style: styles.get(alternativeId)
    })) || []
  );
};

export const getStyleDataForStyleId = state => styleId => {
  const styles = getErrorFreeStyles(state);
  return styles?.get(styleId);
};

export const getStylePDPUrl = state => styleId => {
  const categoryPath = getStyleCategoryPath(state, styleId);
  if (!categoryPath || categoryPath === '/' || !styleId) return null;
  return `${RemoteServices.catalog}${categoryPath}/${styleId}`;
};

export const getAnchorComplementsArray = createSelector(
  [getAnchorComplements],
  c => c?.toArray() || []
);

export const getAnchorAlternativesArray = createSelector(
  [getAnchorAlternatives],
  a => a?.toArray() || []
);

export const isAnchorComplementsLoaded = createSelector(
  [getAnchorComplements],
  c => c !== undefined
);

export const hasAnchorComplements = createSelector(
  [getAnchorComplements],
  c => c?.size > 0
);

export const getAnchorCompatibleStyles = createSelector(
  [getAnchorStyleId, getCompatibleStyles],
  (styleId, compatibleStyles) => compatibleStyles.get(styleId)
);

export const getAnchorCompatibleStylesToArray = createSelector(
  [getAnchorCompatibleStyles],
  compatibleStyles => compatibleStyles?.toArray() || []
);

export const isAnchorCompatibleStylesLoaded = createSelector(
  [getAnchorCompatibleStyles],
  c => c !== undefined
);

export const hasCompatibleStyles = createSelector(
  [getAnchorCompatibleStyles],
  c => c?.size > 0
);

export const getAnchorAlternativeStyles = createSelector(
  [getAnchorAlternativesArray, getStyles],
  (alternatives, styles) => {
    const alternativeStyles = alternatives.map(alternativeId => ({
      alternativeId,
      style: styles.get(alternativeId)
    }));

    return alternativeStyles;
  }
);

export const getAnchorComplementStyles = createSelector(
  [getAnchorComplementsArray, getErrorFreeStyles, hasSleeveDesign],
  (complements, styles, hasSleeveDesign) => {
    let complementStyles = complements.map(complementId => ({
      complementId,
      style: styles.get(complementId)
    }));

    // When a saved design is loaded, its styles are undefined
    const unloadedStyles = complementStyles.filter(
      complement => !complement.style
    );

    if (hasSleeveDesign && unloadedStyles.length === 0) {
      complementStyles = complementStyles.filter(complementStyle =>
        complementStyle.style.views.some(
          view => view.name === 'leftSleeve' || view.name === 'rightSleeve'
        )
      );
    }
    return complementStyles;
  }
);

export const getMissingAnchorComplementStyleIds = state => {
  const complements = getAnchorComplements(state);
  return complements
    ?.filter(complementId => {
      return !getStyle(state, {styleId: complementId}) ? complementId : null;
    })
    ?.filter(complementId => !!complementId)
    ?.toJS?.();
};

export const getAnchorComplementStyleObjects = createSelector(
  [getSelectedProductColor, getAnchorComplementStyles],
  (selectedColor, complements) => {
    return complements?.reduce((complementsAcc, complement) => {
      complementsAcc.push(
        getClosestCandidateColorByRGB({
          currentColor: selectedColor,
          candidateColors: getValidProductColors({
            style: complement?.style
          })
        })
      );
      return complementsAcc;
    }, []);
  }
);

export const allStylesLoaded = createSelector(
  [getSelections, getStyles],
  (selections, styleDictionary) =>
    selections.every(s => styleDictionary.get(s.styleId))
);

export const fullColorViews = createSelector([viewIcons], viewIcons => {
  viewIcons = viewIcons.toJS();
  return Object.keys(viewIcons).reduce((acc, viewId) => {
    acc[viewId] = !!viewIcons[viewId].find(isFullColorUpload);
    return acc;
  }, {});
});

export const getElevatedStyleComplementsByType = state => styleId => {
  const elevatedStyleComplementsByType = {};
  const styles = getErrorFreeStyles(state);
  const selectedProductIds = getSelectedProductsIds(state);
  const style = styles.get(styleId);
  if (!style) return elevatedStyleComplementsByType;

  const productType = style.name.includes('Tall') ? 'Tall' : style.product_type;

  const complements = getComplements(state);

  const complementStyleIds = complements.get(styleId);

  if (!complementStyleIds || complementStyleIds.length === 0) {
    return elevatedStyleComplementsByType;
  }

  complementStyleIds.forEach((complementId, position) => {
    const complementStyle = styles.get(complementId);
    if (!complementStyle) return;
    if (complementStyle.name.includes('Canada')) return;

    const complementProductType = complementStyle.name.includes('Tall')
      ? 'Tall'
      : complementStyle.product_type;

    // Only first position complement is eligible for Women's or Unisex chip
    if (['Unisex', "Women's"].includes(complementProductType) && position > 0) {
      return;
    }

    if (
      !selectedProductIds.includes(complementStyle.id) &&
      productType !== complementProductType &&
      ELEVATED_COMPLEMENT_TYPES.includes(complementProductType) &&
      !elevatedStyleComplementsByType[complementProductType]
    ) {
      elevatedStyleComplementsByType[complementProductType] = complementStyle;
    }
  });

  return elevatedStyleComplementsByType;
};

const getProductViewsColorCount = ({
  style,
  productColor,
  viewIcons,
  viewColors
}) => {
  const styleViews = (style && style.views) || [];
  return viewColors.reduce((count, view, viewId) => {
    if (!styleViews.find(view => view.id === viewId)) {
      return count;
    }

    const pricingGroups = (style && style.pricing_groups) || [];
    const pricingGroup =
      pricingGroups.find(({_id}) => productColor?.pricing_group_id === _id) ||
      {};
    const viewName = ID_VIEW_MAP[viewId];
    const icons = viewIcons.get(viewId);
    // Full color uploads are printed using process ink colors (CMYK) rather than spot colors.
    // Because of flashing required they are priced as 5 colors on a light product, 8 on dark.
    if (icons && icons.find(isFullColorUpload)) {
      count[viewName] = pricingGroup.pricing_factor_f > 0 ? 8 : 5;
    } else {
      count[viewName] = view.size;
    }
    return count;
  }, {});
};

export const anchorColorCount = createSelector(
  [getAnchorStyle, getAnchorColor, viewIcons, viewColors],
  (anchorStyle, anchorColor, viewIcons, viewColors) => {
    if (!anchorColor) return 0;
    return getProductViewsColorCount({
      style: anchorStyle,
      productColor: anchorColor,
      viewIcons,
      viewColors
    });
  }
);

export const getProductColorCount =
  state =>
  ({styleId, colorId}) => {
    const style = getStyle(state, {styleId});
    const productColor = getColor(state, {colorId});
    const viewIcons = viewIcons(state);
    const viewColors = viewColors(state);

    if (!productColor) return 0;

    return getProductViewsColorCount({
      style,
      productColor,
      viewIcons,
      viewColors
    });
  };

export const getMaxColorView = createSelector(
  [anchorColorCount],
  colorCount => {
    const highColorViews = Object.keys(colorCount).filter(
      view => colorCount[view] >= 3
    );
    return highColorViews.reduce((maxColorView, view) => {
      return colorCount[view] > colorCount[maxColorView] ? view : maxColorView;
    }, highColorViews[0]);
  }
);

export const getMaxColorViewColorCount = createSelector(
  [anchorColorCount, getMaxColorView],
  (colorCount, view) => {
    // find the view with the most colors
    // remove a color from that view
    // this.colorCountOfViewMaxColor = reducedColorCount[viewMaxColor];
    return colorCount[view];
  }
);

export const isAnchorOverColorLimit = createSelector(
  [getAnchorStyle, anchorColorCount],
  (style, colorCounts) => {
    const colorLimit = style?.color_limit || Infinity;
    return Object.values(colorCounts).some(viewCount => viewCount > colorLimit);
  }
);

export const getAnchorColorLimit = createSelector(
  [getAnchorStyle],
  style => style?.color_limit || Infinity
);

export const isAnchorOverSleeveColorLimit = createSelector(
  [anchorColorCount],
  colorCounts =>
    ['leftSleeve', 'rightSleeve'].some(
      viewName => colorCounts[viewName] > SLEEVE_COLOR_LIMIT
    )
);

export const getProductNote = state => {
  const selectedView = getSelectedView(state);
  const selection = getCurrentSelection(state);
  const selectedColor = getColor(state, selection);
  if (!selectedColor) return null;
  const view_image =
    selectedColor.view_images.find(
      view_image => view_image.name === selectedView.name
    ) || {};
  return view_image.note;
};

export const showSleevePrintRepImageryNote = state => {
  if (!showSleevePrintNote(state)) return false;

  const selectedProduct = getSelectedProduct(state);
  const productColor = selectedProduct.colors.find(
    item => item?.id === getCurrentColorId(state)
  );
  const colorHasAllProductViewImages = styleColorHasAllProductViewImages({
    style: selectedProduct,
    styleColor: productColor
  });
  return !colorHasAllProductViewImages && canBorrowProductViewImages(state)();
};

export const showSleevePrintNote = state => {
  if (
    getProductNote(state) ||
    !isSleeveDecoEligible(state) ||
    !SLEEVE_VIEW_IDS.includes(selectedViewId(state))
  ) {
    return false;
  }
  return !!getViewsList(state)?.find(v => v?.name?.includes('Sleeve'));
};

export const hasSleeveImagesForProductColor = createSelector(
  [getSelectedProduct, getCurrentColorId],
  (selectedProduct, currentColorId) => {
    if (!selectedProduct?.colors || !currentColorId) return false;

    const productColor = selectedProduct.colors.find(
      item => item?.id === currentColorId
    );
    const leftSleeveImage = getViewImage(
      productColor,
      ID_VIEW_MAP[VIEW_ID_MAP.leftSleeve]
    );
    const rightSleeveImage = getViewImage(
      productColor,
      ID_VIEW_MAP[VIEW_ID_MAP.rightSleeve]
    );
    return leftSleeveImage?.uri || rightSleeveImage?.uri;
  }
);

export const remainingProductsHaveSleeveProductImageIssue =
  state => droppedProductColorId => {
    const selectedProductColors = getColorSelections(state);
    let allProductsWillHaveIssue = true;

    selectedProductColors.forEach(productColorId => {
      const hasIssueWithSleeveImage =
        productColorHasSleeveProductImageIssue(state)(productColorId);

      if (
        droppedProductColorId !== productColorId &&
        !hasIssueWithSleeveImage
      ) {
        allProductsWillHaveIssue = false;
      }
    });

    return allProductsWillHaveIssue;
  };

export const hasSleeveProductImageIssue = state => {
  const productColor = getSelectedProductColor(state);
  return productColorHasSleeveProductImageIssue(state)(productColor?.id);
};

export const productColorHasSleeveProductImageIssue =
  state => productColorId => {
    const hasInvalidProductImage = getHasInvalidProductImage(state);
    const leftSleeveKey = getProductIdViewIdKey({
      productId: productColorId,
      viewId: VIEW_ID_MAP.leftSleeve
    });
    const rightSleeveKey = getProductIdViewIdKey({
      productId: productColorId,
      viewId: VIEW_ID_MAP.rightSleeve
    });
    return !!(
      hasInvalidProductImage[leftSleeveKey] ||
      hasInvalidProductImage[rightSleeveKey]
    );
  };

export const isAtProductLimit = createSelector(
  [getSelections, isFundraisingMode],
  (selections, fundraisingMode) => {
    const currentCount = selections.size;
    if (fundraisingMode) {
      return currentCount >= FUNDRAISING_MAXIMUM_PRODUCT_COUNT;
    } else {
      return currentCount >= DEFAULT_MAXIMUM_PRODUCT_COUNT;
    }
  }
);

export const getDefaultQuantities = createSelector(
  [getQuantities],
  quantities =>
    Object.entries(quantities.toJS()).reduce((acc, [productId, sizes]) => {
      if (!productId || !sizes) {
        return acc;
      }

      const productTotal = Object.keys(sizes).reduce(
        (sum, size) => sum + sizes[size],
        0
      );
      const firstSizeKey = Object.keys(sizes)[0];
      const defaultQuantityFirstSize = {
        [firstSizeKey]: productTotal
          ? sizes[firstSizeKey]
          : DEFAULT_QUOTE_QUANTITY
      };
      acc[productId] = {...sizes, ...defaultQuantityFirstSize};
      return acc;
    }, {})
);

export const getRealTimePriceQuote = createSelector(
  [getCurrentColorId, getQuantities, getTotalQuantity],
  (selectedColorId, quantities, totalQuantity) => {
    let firstSize;
    const productQuantity = quantities
      ?.find((value, key) => key === selectedColorId)
      ?.reduce((acc, count) => acc + count, 0);

    if (productQuantity === 0) {
      const selectedProductQuantities = quantities
        ?.get(selectedColorId)
        ?.map((quantity, size) => {
          if (!firstSize) {
            firstSize = size;
            return totalQuantity;
          }
          return 0;
        });
      return {
        [selectedColorId]: selectedProductQuantities.toJS()
      };
    }

    return quantities?.toJS();
  }
);

export const getTotalDefaultQuantity = createSelector(
  [getDefaultQuantities],
  quantities =>
    Object.keys(quantities).reduce((total, productId) => {
      return (
        total +
        Object.keys(quantities[productId]).reduce((quantity, size) => {
          return quantity + quantities[productId][size];
        }, 0)
      );
    }, 0)
);

export const getNamesAndNumbersWcibmo = createSelector(
  [getNamesAndNumbersCount, getQuantitiesJs],
  (namesAndNumbersCount, quantities) => {
    if (!namesAndNumbersCount || !(namesAndNumbersCount > 0)) {
      return null;
    }

    const totalQuantity = Object.values(quantities).reduce((count, item) => {
      return (
        count +
        Object.values(item).reduce((count, item) => {
          return count + item;
        }, 0)
      );
    }, 0);

    if (totalQuantity < namesAndNumbersCount) {
      return WCIBMOS.REMOVE_NAMES_AND_NUMBERS;
    } else {
      return null;
    }
  }
);

export const allowIndividualShipping = createSelector(
  [getSelectedProducts],
  selectedProducts =>
    selectedProducts?.every(product => product?.individual_ship_eligible)
);

export const getSubtleColorsList = createSelector(
  [getSelectedProductColor],
  productColor => {
    if (!productColor) return [];
    const prodColor = new MMSColor({
      rgbs: [
        {
          red: productColor.red,
          green: productColor.green,
          blue: productColor.blue
        }
      ],
      subtle_inks: [productColor.subtle_inks]
    });

    if (!isValidColor(prodColor)) return [];

    const nonNoneColors = ORDERED_INK_COLORS.filter(
      color => color.name !== 'None'
    );

    const v1SubtleInks = nonNoneColors.filter(color =>
      prodColor.isSubtleInk(color.id)
    );

    if (v1SubtleInks.length > 0) {
      return v1SubtleInks;
    }

    return nonNoneColors.filter(color =>
      prodColor.isSubtleRgb({
        red: color.red,
        green: color.green,
        blue: color.blue
      })
    );
  }
);

// Drop one side / drop sleeves CTAs
export const getMaxSaveAmountFromDroppedViews =
  state =>
  ({droppedViewsIds}) => {
    if (!droppedViewsIds || !Array.isArray(droppedViewsIds)) return 0;
    let maxSaveAmount = 0;
    droppedViewsIds.forEach(droppedViewId => {
      const droppedViewSaveAmount = getSaveAmountFromViewInfo({
        viewInfo: getDroppedViewInfo(state)({productViewId: droppedViewId})
      });
      if (droppedViewSaveAmount) {
        maxSaveAmount =
          droppedViewSaveAmount > maxSaveAmount
            ? droppedViewSaveAmount
            : maxSaveAmount;
      }
    });
    return maxSaveAmount;
  };

export const getSaveAmountFromDroppedSleeves = state => () => {
  const totalPrice = getTotalPrice(state);
  const sleeveViewLessPrice = getSleeveViewLessPrice(state);
  const sleeveViewLessSaveAmount = getSleeveViewLessSaveAmount(state);

  if (totalPrice && sleeveViewLessPrice) {
    return Math.round((1 - sleeveViewLessPrice / totalPrice) * 100);
  }

  if (sleeveViewLessSaveAmount) return sleeveViewLessSaveAmount;

  const savedAmountLeftSleeve =
    getSaveAmountFromViewInfo({
      viewInfo: getDroppedViewInfo(state)({
        productViewId: VIEW_ID_MAP.leftSleeve
      })
    }) || 0;
  const savedAmountRightSleeve =
    getSaveAmountFromViewInfo({
      viewInfo: getDroppedViewInfo(state)({
        productViewId: VIEW_ID_MAP.rightSleeve
      })
    }) || 0;
  return savedAmountLeftSleeve + savedAmountRightSleeve;
};

export const getDroppedViewInfo =
  state =>
  ({productViewId}) => {
    const anchorStyle = getAnchorStyle(state);
    const productViews = getProductViews(state)(anchorStyle);
    const itemizedPricesWithoutDroppedView =
      getItemizedPricesWithoutDroppedView(state);

    if (!productViews || !itemizedPricesWithoutDroppedView || !productViewId) {
      return null;
    }

    const droppedView = productViews.find(view => view?.id === productViewId);

    if (!droppedView?.name) return null;

    const droppedViewPrices = itemizedPricesWithoutDroppedView.find(
      (_, droppedViewName) => droppedViewName === droppedView.name
    );

    return {
      id: droppedView.id,
      name: droppedView.name,
      sumSubtotalsBefore: droppedViewPrices?.get('sumSubtotalsBefore'),
      sumSubtotalsAfter: droppedViewPrices?.get('sumSubtotalsAfter')
    };
  };

export const getSaveAmountFromDroppedView = state => () => {
  if (!getHasDroppedView(state)) return null;
  return getSaveAmountFromViewInfo({viewInfo: getDroppedView(state)});
};

export const getSaveAmountFromViewInfo = ({viewInfo}) => {
  if (!viewInfo) return null;

  const sumSubtotalsBefore = parseFloat(viewInfo.sumSubtotalsBefore);
  const sumSubtotalsAfter = parseFloat(viewInfo.sumSubtotalsAfter);

  if (isNaN(sumSubtotalsBefore) || isNaN(sumSubtotalsAfter)) return null;

  const amount = Math.round((1 - sumSubtotalsAfter / sumSubtotalsBefore) * 100);

  return amount > 0 ? amount : null;
};

export const getRemainingProductView =
  state =>
  ({droppedProductViewId}) => {
    const anchorStyle = getAnchorStyle(state);
    const productViews = getProductViews(state)(anchorStyle);

    if (!productViews || !droppedProductViewId) return null;

    const remainingViews = productViews.filter(
      item => item.id !== droppedProductViewId
    );

    return remainingViews.length > 0 ? remainingViews[0] : null;
  };

export const getProductViewIcons = ({icons, productViewId}) => {
  return icons
    .map(icon => {
      return icon && icon?.viewId === productViewId ? icon?.id : null;
    })
    .filter(iconId => iconId !== null);
};

// Avoid unnecessary expensive calculations in the getSimilarProductWithImages selector
const similarProducts = {};

export const getSimilarProductWithImages =
  state => (styleId, productColorId) => {
    if (!styleId || !productColorId) return null;

    const key = getStyleIdProductColorIdKey({styleId, productColorId});

    if (similarProducts[key]) return similarProducts[key];

    const selectedStyles = getSelectedProducts(state);
    const style = selectedStyles?.toJS?.().find(style => style.id === styleId);

    if (!style) return null;

    const similarProduct = getSimilarProductColorForStyle({
      style: style,
      currentColorId: productColorId
    });
    similarProducts[key] = similarProduct;

    return similarProduct;
  };

export const getSimilarProductColorForStyle = ({style, currentColorId}) => {
  if (!style?.colors) return null;

  const candidateColors = [];
  let currentColor = null;

  for (let i = 0; i < style.colors.length; i++) {
    const productColor = style.colors[i];

    if (
      styleColorHasAllProductViewImages({
        style,
        styleColor: productColor
      })
    ) {
      candidateColors.push(productColor);
    }
    if (currentColorId && productColor?.id === currentColorId) {
      currentColor = productColor;
    }
  }

  return candidateColors.length > 0
    ? getClosestCandidateColorByRGB({currentColor, candidateColors})
    : null;
};

export const styleColorHasAllProductViewImages = ({style, styleColor}) => {
  if (!style || !styleColor) return false;

  let colorHasAllImages = true;
  style.views?.forEach(view => {
    const productViewImage = getViewImage(styleColor, view?.name);
    if (!productViewImage?.uri) colorHasAllImages = false;
  });

  return colorHasAllImages;
};

export const selectSelectedAlternateVariantStyles = createSelector(
  [getSelectedProducts, getErrorFreeStyles],
  (selectedProducts, styles) => {
    const selectedAlternateVariantStyles = {};
    selectedProducts.forEach(p => {
      const alternateVariantStyle = styles.get(
        getAlternateVariant(p)?.style_id?.toString()
      );
      if (alternateVariantStyle) {
        selectedAlternateVariantStyles[alternateVariantStyle.id] =
          alternateVariantStyle;
      }
    });

    return selectedAlternateVariantStyles;
  }
);

export const getAnchorPrintMethodStyleIds = createSelector(
  [getAnchorStyle],
  anchorStyle => {
    return anchorStyle?.variants?.reduce((prev, curr) => {
      prev[curr.decoration_method] = curr.style_id?.toString();
      return prev;
    }, {});
  }
);

// Laser engraving
export const isAnchorLaserEngraved = createSelector(
  [getAnchorDecorationMethod],
  anchorDecorationMethod => anchorDecorationMethod === 'laser engraved'
);

export const getDecorationOverride = createSelector(
  [getAnchorStyleId, getAnchorDecorationMethod, getAnchorMaterial],
  (styleId, decorationMethod, material) =>
    matchesDecorationOverride(styleId, decorationMethod, material)
);

export const getDecorationOverrideColor = createSelector(
  [decorationOverridesEnabled, getDecorationOverride, getAnchorColor],
  (isDecorationOverrideEnabled, decorationOverride, styleColor) => {
    if (!isDecorationOverrideEnabled || !decorationOverride || !styleColor) {
      return null;
    }
    const styleColorRecord = recordFromMmsColor(styleColor);
    if (
      decorationOverride.enforceColor &&
      decorationOverride.enforceColor in INK_COLORS
    ) {
      return INK_COLORS[decorationOverride.enforceColor];
    }

    return findClosestColorConsideringBrightness(
      styleColorRecord,
      ORDERED_INK_COLORS
    );
  }
);

export const getDecorationOverrideColorByStyleId = state => styleId => {
  const style = getStyle(state, {styleId});
  if (!style) return null;

  return matchesDecorationOverride(
    styleId,
    style.decoration_method,
    style.material
  );
};

export const getDecorationOverrideColorForStyle = state => (style, colorId) => {
  const styleColor = style.colors.find(c => c.id === colorId);
  const styleColorRecord = recordFromMmsColor(styleColor);
  const decorationOverride = getDecorationOverrideColorByStyleId(state)(
    style.id
  );

  if (!decorationOverride) {
    return null;
  }

  if (
    decorationOverride.enforceColor &&
    decorationOverride.enforceColor in INK_COLORS
  ) {
    return INK_COLORS[decorationOverride.enforceColor];
  }

  return findClosestColorConsideringBrightness(
    styleColorRecord,
    ORDERED_INK_COLORS
  );
};

export const getLaserEngravedDesignColor = createSelector(
  [
    getAnchorColor,
    isAnchorLaserEngraved,
    anchorHasWoodenMaterial,
    anchorIsLeatherPatch
  ],
  (anchorColor, isLaserEngraved, isWoodenProduct, isLeatherPatch) => {
    if (!isLaserEngraved) return null;
    if (isWoodenProduct) {
      return getBrownForAnchorColor({anchorColor}) || INK_COLORS.Brown;
    }
    if (isLeatherPatch) return INK_COLORS.Black;
    return INK_COLORS.Gray;
  }
);

export const shouldRearrangeIconsOnProductSwap =
  state =>
  ({previousProduct, nextProduct}) => {
    if (!previousProduct?.styleId || !nextProduct?.styleId) {
      return true;
    }

    if (
      previousProduct.styleId === nextProduct.styleId ||
      areComplementProducts(state)({
        previousProduct,
        nextProduct
      })
    ) {
      return false;
    }

    const areCompatibles = areCompatibleProducts(state)({
      previousProduct,
      nextProduct
    });
    const previousProductStyle = getStyle(state, {
      styleId: previousProduct.styleId.toString()
    });
    const nextProductStyle = getStyle(state, {
      styleId: nextProduct.styleId.toString()
    });

    if (
      areCompatibles &&
      previousProductStyle?.primary_category_id ===
        nextProductStyle?.primary_category_id
    ) {
      return false;
    }

    return true;
  };

export const getAllProductsAllowBlanks = createSelector(
  [getSelectedProductsJs],
  selectedProducts => selectedProducts.every(p => p?.allow_blank)
);

export const isUnquoteable = createSelector(
  [isAnchorOverColorLimit, getAllProductsAllowBlanks, isBlank],
  (overloaded, allowBlanks, isBlankDesign) => {
    if (overloaded) {
      return true;
    }

    if (isBlankDesign && !allowBlanks) {
      return true;
    }

    return false;
  }
);
